import { Skeleton } from './skeleton'
import { motion } from 'framer-motion'

export function MeetingCardSkeleton() {
  return (
    <motion.div
      animate={{ opacity: [0.3, 0.6, 0.3] }}
      transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0, 0.2, 1] }}
      className="bg-card border border-border rounded-2xl p-4 flex items-center gap-4"
    >
      <Skeleton className="w-10 h-10 rounded-xl shrink-0" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-2/3 rounded-lg" />
        <Skeleton className="h-3 w-1/2 rounded-lg" />
      </div>
      <Skeleton className="w-16 h-6 rounded-lg shrink-0" />
    </motion.div>
  )
}

export function MeetingListSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="space-y-3 p-1">
      {Array.from({ length: count }).map((_, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: i * 0.05 }}
          className="flex items-center gap-3 p-3 rounded-2xl bg-card border border-stroke-100 dark:border-border"
        >
          <motion.div
            animate={{ opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0, 0.2, 1] }}
          >
            <Skeleton className="w-10 h-10 rounded-full shrink-0" />
          </motion.div>
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3 w-20 rounded-lg" />
            <Skeleton className="h-4 w-32 rounded-lg" />
            <Skeleton className="h-3 w-24 rounded-lg" />
          </div>
          <div className="flex gap-1">
            <Skeleton className="w-6 h-6 rounded-full" />
            <Skeleton className="w-6 h-6 rounded-full" />
            <Skeleton className="w-6 h-6 rounded-full" />
          </div>
        </motion.div>
      ))}
    </div>
  )
}

export function StatCardSkeleton() {
  return (
    <motion.div
      animate={{ opacity: [0.3, 0.6, 0.3] }}
      transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0, 0.2, 1] }}
      className="bg-card border border-border rounded-2xl p-4 space-y-3"
    >
      <Skeleton className="w-8 h-8 rounded-xl" />
      <Skeleton className="h-6 w-16 rounded-lg" />
      <Skeleton className="h-3 w-24 rounded-lg" />
    </motion.div>
  )
}

export function StatRowSkeleton({ count = 4 }: { count?: number }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="grid grid-cols-2 sm:grid-cols-4 gap-3"
    >
      {Array.from({ length: count }).map((_, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: i * 0.05 }}
        >
          <StatCardSkeleton />
        </motion.div>
      ))}
    </motion.div>
  )
}

export function TableRowSkeleton() {
  return (
    <motion.div
      animate={{ opacity: [0.3, 0.6, 0.3] }}
      transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0, 0.2, 1] }}
      className="flex items-center gap-4 px-4 py-3 border-b border-border/50"
    >
      <Skeleton className="w-8 h-8 rounded-full shrink-0" />
      <div className="flex-1 space-y-1.5">
        <Skeleton className="h-3.5 w-40 rounded-lg" />
        <Skeleton className="h-3 w-28 rounded-lg" />
      </div>
      <Skeleton className="w-14 h-5 rounded-full" />
      <Skeleton className="w-10 h-4 rounded-lg hidden sm:block" />
    </motion.div>
  )
}

export function TableSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="bg-card border border-border rounded-2xl overflow-hidden"
    >
      <div className="px-4 py-3 border-b border-border">
        <Skeleton className="h-4 w-32 rounded-lg" />
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <TableRowSkeleton key={i} />
      ))}
    </motion.div>
  )
}

export function ChartSkeleton({ height = 220 }: { height?: number }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="bg-card border border-border rounded-2xl p-4 space-y-3"
    >
      <Skeleton className="h-4 w-40 rounded-lg" />
      <motion.div
        animate={{ opacity: [0.3, 0.6, 0.3] }}
        transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0, 0.2, 1] }}
      >
        <Skeleton className="rounded-xl w-full" style={{ height }} />
      </motion.div>
    </motion.div>
  )
}

export function WebinarCardSkeleton() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-card border border-border rounded-2xl p-4 space-y-3"
    >
      <div className="flex items-start justify-between">
        <div className="space-y-2 flex-1">
          <Skeleton className="h-4 w-3/4 rounded-lg" />
          <Skeleton className="h-3 w-1/2 rounded-lg" />
        </div>
        <Skeleton className="w-16 h-5 rounded-full" />
      </div>
      <div className="flex gap-4">
        <Skeleton className="h-3 w-20 rounded-lg" />
        <Skeleton className="h-3 w-16 rounded-lg" />
      </div>
      <div className="flex gap-2 pt-1">
        <Skeleton className="h-8 flex-1 rounded-xl" />
        <Skeleton className="h-8 flex-1 rounded-xl" />
      </div>
    </motion.div>
  )
}

export function ProfileSkeleton() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="flex items-center gap-4 p-4"
    >
      <motion.div
        animate={{ opacity: [0.3, 0.6, 0.3] }}
        transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0, 0.2, 1] }}
      >
        <Skeleton className="w-16 h-16 rounded-full shrink-0" />
      </motion.div>
      <div className="space-y-2">
        <Skeleton className="h-5 w-36 rounded-lg" />
        <Skeleton className="h-3.5 w-48 rounded-lg" />
        <Skeleton className="h-3 w-24 rounded-lg" />
      </div>
    </motion.div>
  )
}
